import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlignRight, Calendar, ChartBarHorizontal } from '@phosphor-icons/react';
import { Button } from 'antd';
import { Plus } from '@phosphor-icons/react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ListFilter } from '../../../../../components';
import { useDebouncedEffect, WaveListHook } from '../../../../../hooks';
import { WaveAntTable } from '../../../../../page-components';
import { CommonUtility, DateFormat, DateUtility } from '../../../../../utility';
import WavesExportPopup from '../../../../../page-components/waves/WavesExportPopup';
import { WaveSearchInput } from '../../../../../page-components/waves/WaveSearch';
import { WavesTabToolbar } from '../../../../../page-components/waves/waves-tab-toolbar';
import { useWavesTabExtraContent } from '../../../../../contexts/waves-tab-extra';
import { useWavesFilterContext } from './waves-list-context';

const calendarOptions = [{ value: 'BETWEEN', label: 'Between' }];

const presetOptions = [
  { label: 'Today', value: 'today' },
  { label: 'Yesterday', value: 'yesterday' },
];

const analyticsStatusOptions = [
  { label: 'All', value: 'building,ready,sorted,completed,cancelled' },
  { label: 'Completed', value: 'completed' },
  { label: 'Ready', value: 'ready' },
  { label: 'Building', value: 'building' },
  { label: 'Sorted', value: 'sorted' },
  { label: 'Cancelled', value: 'cancelled' },
];

function buildAnalyticsFilterFromQuery(searchParams) {
  const query = Object.fromEntries(searchParams.entries());
  const defaultDateRange = DateUtility.getWeekToDateRange();

  return {
    dateRange: query.dateRange || defaultDateRange,
    dateRangeOp: 'BETWEEN',
    status: query.status || 'completed',
    presets: query.presets || undefined,
    page: CommonUtility.isValidNumber(query.page) ? Number(query.page) : 1,
    limit: CommonUtility.isValidNumber(query.limit) ? Number(query.limit) : 10,
  };
}

export function AnalyticsTab({ warehouseId, search, setAnalyticsParams }) {
  const {
    createWave,
    onWaveSearchSelect,
    setSearch: setTableSearch,
  } = useWavesFilterContext();
  const [searchParams] = useSearchParams();
  const initialFilter = useMemo(
    () => buildAnalyticsFilterFromQuery(searchParams),
    // biome-ignore lint/correctness/useExhaustiveDependencies: seed once on mount
    [],
  );

  const {
    data,
    totalData,
    loading,
    filter,
    filterChanged,
    pageChanged,
    updateAllFilter,
    params,
  } = WaveListHook(null, warehouseId, search, 0, initialFilter);

  const navigate = useNavigate();
  const [refreshKey, setRefreshKey] = useState(0);
  const didHydrateFromUrl = useRef(false);

  useEffect(() => {
    if (params.status && params.startDate && params.endDate) {
      setAnalyticsParams(params);
    }
  }, [params, setAnalyticsParams]);

  useDebouncedEffect(
    () => {
      if (!params.status || !params.startDate || !params.endDate) {
        setRefreshKey(Math.random());
      }
    },
    [params],
    300,
  );

  const [showExportPopup, setShowExportPopup] = useState(false);

  const setFilter = useCallback(
    next => {
      const updated = { ...next, page: 1 };
      if (updated.dateRange && updated.dateRange !== filter.dateRange) {
        updated.presets = undefined;
      }
      if (updated.presets && updated.presets !== filter.presets) {
        updated.dateRange = undefined;
      }
      updateAllFilter(updated);
    },
    [filter.dateRange, filter.presets, updateAllFilter],
  );

  useEffect(() => {
    const urlObject = {};
    const ignoreDefaults = {
      status: 'completed',
      limit: 10,
      page: 1,
    };

    Object.keys(filter).forEach(key => {
      const value = filter[key];
      if (value != null && value !== '' && ignoreDefaults[key] !== value) {
        urlObject[key] = value;
      }
    });

    const nextSearch = new URLSearchParams(urlObject).toString();
    if (searchParams.toString() === nextSearch) return;

    navigate({ search: nextSearch }, { replace: true });
  }, [filter, navigate, searchParams]);

  const filters = useMemo(() => {
    const query = Object.fromEntries(searchParams.entries());
    const temp = { ...query };
    Object.keys(temp).forEach(key => {
      const final = CommonUtility.tryParseJSONObject(temp[key]);
      if (final) {
        temp[key] = final;
      }
    });

    const defaultValues = { ...temp };
    const defaultDateRange = DateUtility.getWeekToDateRange();

    return [
      {
        name: 'dateRange',
        label: 'Date Range',
        icon: <Calendar />,
        type: 'date',
        dateFormat: DateFormat.yearMonthDate,
        queryOptions: calendarOptions,
        typeQuery: 'dateRangeOp',
        defaultQuery: 'BETWEEN',
        defaultValue: defaultValues.dateRange || defaultDateRange,
      },
      {
        name: 'status',
        label: 'Status',
        icon: <ChartBarHorizontal />,
        options: analyticsStatusOptions,
        search: true,
        sorted: false,
        defaultValue: defaultValues.status || 'completed',
      },
      {
        name: 'presets',
        label: 'Presets',
        icon: <AlignRight />,
        sorted: false,
        options: presetOptions,
        defaultValue: defaultValues.presets || null,
      },
      {
        skip: true,
        name: 'page',
        label: 'Page',
        icon: null,
        defaultValue: defaultValues.page || 1,
      },
      {
        skip: true,
        name: 'limit',
        label: 'Limit',
        icon: null,
        defaultValue: CommonUtility.isValidNumber(defaultValues.limit)
          ? Number(defaultValues.limit)
          : 10,
      },
    ];
  }, [refreshKey, searchParams]);

  useEffect(() => {
    if (refreshKey === 0 && didHydrateFromUrl.current) return;
    if (refreshKey === 0) {
      didHydrateFromUrl.current = true;
    }
    updateAllFilter(buildAnalyticsFilterFromQuery(searchParams));
  }, [refreshKey, searchParams, updateAllFilter]);

  useWavesTabExtraContent(
    () => (
      <WavesTabToolbar>
        <Button
          variant="solid"
          color="default"
          icon={<Plus size={16} weight="bold" />}
          onClick={createWave}
        >
          Wave
        </Button>
        <WaveSearchInput
          onResultClick={onWaveSearchSelect}
          setTableSearch={setTableSearch}
          warehouseId={warehouseId}
        />
        <Button type="primary" onClick={() => setShowExportPopup(true)}>
          Export Wave Report
        </Button>
        <ListFilter filters={filters} filter={filter} setFilter={setFilter} />
      </WavesTabToolbar>
    ),
    [
      createWave,
      onWaveSearchSelect,
      setTableSearch,
      warehouseId,
      filters,
      filter,
      setFilter,
      setShowExportPopup,
    ],
  );

  return (
    <div className="mt-4">
      <WaveAntTable
        loading={loading}
        data={data}
        totalData={totalData}
        filter={filter}
        filterChanged={filterChanged}
        pageChanged={pageChanged}
      />
      <WavesExportPopup
        exportPopup={showExportPopup}
        setExportPopup={setShowExportPopup}
        search={params}
      />
    </div>
  );
}
